import React, { useState } from "react";
import {
  Edit,
  Trash2,
  MapPin,
  Phone,
  GraduationCap,
  UserPlus,
  Briefcase,
  UserMinus,
  User,
} from "lucide-react";
import Select from "react-select";

const OpportunityCard = ({
  opportunity,
  onEdit,
  onDelete,
  onAddStudent,
  onRemoveStudent,
  students,
  timeLogs,
}) => {
  const [showStudents, setShowStudents] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  const toggleStudents = () => {
    setShowStudents(!showStudents);
  };

  const handleAddStudent = () => {
    if (selectedStudent) {
      onAddStudent(opportunity.id, selectedStudent.value);
      setSelectedStudent(null);
    }
  };

  const studentOptions = students
    .filter((student) => {
      if (!opportunity.students) return true;
      return !opportunity.students.includes(student.studentId);
    })
    .map((student) => ({
      value: student.studentId,
      label: `[${student.studentId}] ${student.firstName} ${student.lastName}`,
    }));

  // Helper function to format time
  const formatTime = (time) => {
    return new Date(`2000-01-01T${time}`).toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const calculateHours = (startTime, endTime) => {
    const [startHour, startMinute] = startTime.split(":").map(Number);
    const [endHour, endMinute] = endTime.split(":").map(Number);

    const totalMinutes =
      endHour * 60 + endMinute - (startHour * 60 + startMinute);
    return totalMinutes / 60;
  };

  const getStudentApprovedHours = (studentId) => {
    const studentLogs = timeLogs.filter(
      (log) =>
        log.studentId === studentId &&
        log.opportunityId === opportunity.id &&
        log.status === "approved"
    );

    const total = studentLogs.reduce((sum, log) => {
      return sum + calculateHours(log.startTime, log.endTime);
    }, 0);

    return Number(total.toFixed(1));
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden flex flex-col h-full relative">
      {/* Header Section */}
      <div className="bg-gray-100 p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
        <h3 className="text-lg font-bold break-words w-full sm:w-auto">{opportunity.hostName}</h3>
        <span className="bg-green-200 text-green-800 text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap">
          Active
        </span>
      </div>

      {/* Content Section */}
      <div className="p-4 flex-grow">
        <div className="mb-4 flex items-center">
          <Briefcase className="text-gray-400 mr-2" size={20} />
          <span className="text-sm text-gray-600 capitalize">
            {opportunity.opportunityType}
          </span>
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="flex items-start">
            <MapPin
              className="text-gray-400 mr-2 mt-1 flex-shrink-0"
              size={20}
            />
            <span className="text-sm text-gray-600">
              {opportunity.streetAddress}, {opportunity.city},{" "}
              {opportunity.stateProvince} {opportunity.postalCode}
            </span>
          </div>
          <div className="flex items-start">
            <Phone className="text-gray-400 mr-2" size={20} />
            <a
              href={`tel:${opportunity.hostPhoneNumber}`}
              className="text-sm text-gray-600 hover:text-blue-600 transition-colors"
            >
              {opportunity.hostPhoneNumber}
            </a>
          </div>
          {opportunity.contactPerson && (
            <div className="flex items-start">
              <User className="text-gray-400 mr-2" size={20} />
              <span className="text-sm text-gray-600">
                {opportunity.contactPerson}
              </span>
            </div>
          )}
          <div className="col-span-2 mt-2">
            <h4 className="text-sm font-semibold text-gray-700 mb-1">
              Required Dress
            </h4>
            <p className="text-sm text-gray-600 capitalize">
              {opportunity.requiredDress}
            </p>
          </div>
          <div className="col-span-2 mt-2">
            <h4 className="text-sm font-semibold text-gray-700 mb-1">
              Schedule
            </h4>
            <div className="flex flex-wrap gap-2">
              {opportunity.schedule ? (
                Object.entries(opportunity.schedule).map(([day, times]) => (
                  <span
                    key={day}
                    className="bg-blue-100 text-blue-800 text-xs px-3 py-1 rounded-full flex items-center whitespace-nowrap"
                  >
                    {day}{" "}
                    <span className="ml-1 text-blue-600">
                      {formatTime(times.startTime)}-{formatTime(times.endTime)}
                    </span>
                  </span>
                ))
              ) : (
                <span className="text-gray-500">No schedule available</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer Section */}
      <div className="bg-gray-30 border-t border-gray-200 mt-auto p-4 flex items-center justify-between text-sm font-semibold text-blue-500 hover:text-blue-600 transition-colors duration-200">
        <button onClick={toggleStudents} className="flex space-x-2">
          <GraduationCap size={20} className="mr-2" />
          {opportunity.students?.length}
        </button>
        <div className="flex items-center space-x-2">
          <Edit
            size={18}
            className="text-blue-500 cursor-pointer hover:text-blue-600 transition-colors duration-200"
            onClick={onEdit}
          />
          <Trash2
            size={18}
            className="text-gray-500 cursor-pointer hover:text-gray-600 transition-colors duration-200"
            onClick={onDelete}
          />
        </div>
      </div>

      {/* Student List Modal */}
      {showStudents && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-1/2">
            <h2 className="text-xl font-bold mb-4 uppercase">
              {opportunity.hostName} - Associated Students
            </h2>

            <div className="bg-white rounded-lg overflow-hidden my-8">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Student ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Student Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Completed hours
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {opportunity.students.map((studentId) => {
                    const student = students.find(
                      (s) => s.studentId === studentId
                    );
                    return (
                      <tr
                        key={studentId}
                        className="hover:bg-gray-100 transition duration-300"
                      >
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                          {studentId}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                          {student
                            ? `${student.firstName} ${student.lastName}`
                            : "Deleted student"}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                          {getStudentApprovedHours(studentId)} hours
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                          <button
                            onClick={() =>
                              onRemoveStudent(opportunity.id, studentId)
                            }
                            className="text-red-500 hover:text-red-700 transition-colors duration-200"
                            title="Remove student"
                          >
                            <UserMinus size={18} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex items-center space-x-2 mb-4">
              <Select
                options={studentOptions}
                value={selectedStudent}
                onChange={setSelectedStudent}
                placeholder="Select a student"
                className="flex-grow"
              />
              <button
                onClick={handleAddStudent}
                className="bg-green-500 text-white p-2 rounded-md hover:bg-green-600 transition duration-300"
                disabled={!selectedStudent}
              >
                <UserPlus size={20} />
              </button>
            </div>

            <button
              onClick={toggleStudents}
              className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition duration-300"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OpportunityCard;